/**
 * Import Sources Hooks
 * Manage import_sources, import_runs, and related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type ImportSource = Database['public']['Tables']['import_sources']['Row'];
type ImportRun = Database['public']['Tables']['import_runs']['Row'];
type ImportSourceInsert = Database['public']['Tables']['import_sources']['Insert'];
type ImportSourceUpdate = Database['public']['Tables']['import_sources']['Update'];

// ============================================
// IMPORT SOURCES
// ============================================

export function useImportSources() {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['import-sources', activeShop?.id],
    queryFn: async () => {
      if (!activeShop?.id) return [];
      
      const { data, error } = await supabase
        .from('import_sources')
        .select('*')
        .eq('shop_id', activeShop.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ImportSource[];
    },
    enabled: !!activeShop?.id,
  });
}

export function useCreateImportSource() {
  const queryClient = useQueryClient();
  const { activeShop } = useAuth();

  return useMutation({
    mutationFn: async (source: Omit<ImportSourceInsert, 'shop_id'>) => {
      if (!activeShop?.id) throw new Error('No active shop');

      const { data, error } = await supabase
        .from('import_sources')
        .insert({
          ...source,
          shop_id: activeShop.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
    },
  });
}

export function useUpdateImportSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ImportSourceUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('import_sources')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
    },
  });
}

export function useDeleteImportSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceId: string) => {
      const { error } = await supabase
        .from('import_sources')
        .delete()
        .eq('id', sourceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
    },
  });
}

export function useToggleImportSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('import_sources')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
    },
  });
}

// ============================================
// IMPORT RUNS
// ============================================

export function useImportRuns(sourceId?: string) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['import-runs', activeShop?.id, sourceId],
    queryFn: async () => {
      if (!activeShop?.id) return [];

      let query = supabase
        .from('import_runs')
        .select(`
          *,
          import_sources!inner(name, source_type)
        `)
        .eq('shop_id', activeShop.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (sourceId) {
        query = query.eq('source_id', sourceId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as (ImportRun & { import_sources: { name: string; source_type: string } })[];
    },
    enabled: !!activeShop?.id,
  });
}

export function useImportRunDetails(runId: string | undefined) {
  return useQuery({
    queryKey: ['import-run-details', runId],
    queryFn: async () => {
      if (!runId) return null;

      const { data: run, error: runError } = await supabase
        .from('import_runs')
        .select(`
          *,
          import_sources(name, source_type)
        `)
        .eq('id', runId)
        .single();

      if (runError) throw runError;

      // Get staging rows for this run
      const { data: stagingRows, error: stagingError } = await supabase
        .from('import_staging')
        .select('*')
        .eq('run_id', runId)
        .order('row_number', { ascending: true });

      if (stagingError) throw stagingError;

      return {
        ...run,
        staging_rows: stagingRows,
      };
    },
    enabled: !!runId,
  });
}

// ============================================
// IMPORT SOURCE TYPE HELPERS
// ============================================

export const SOURCE_TYPE_CONFIG = {
  s3_csv: {
    label: 'S3 CSV',
    icon: 'FileSpreadsheet',
    description: 'Poll S3 bucket for CSV files',
    fields: ['bucket_name', 'prefix', 'region'],
  },
  sftp_csv: {
    label: 'SFTP CSV',
    icon: 'Server',
    description: 'Poll SFTP server for CSV files',
    fields: ['host', 'port', 'username', 'path'],
  },
  api_webhook: {
    label: 'API Webhook',
    icon: 'Webhook',
    description: 'Receive push notifications via webhook',
    fields: ['endpoint_path'],
  },
} as const;

export type ImportSourceType = keyof typeof SOURCE_TYPE_CONFIG;
